/**
 * TeamsPage
 *
 * Teams management page (May 2026 Figma redesign).
 *
 * Toolbar mirrors Talk groups (search icon-button, "Teams" + "N TOTAL"
 * subtitle, overflow `…` button, "+ Add team" pill, view-toggle) so the
 * two siblings under Comms feel cohesive.
 *
 * Table columns:
 *   select / TEAM NAME / TALK GROUPS (pills) / HOME GROUP / MEMBERS / actions
 *
 * Backend: no Team resource exists yet — teams are persisted client-side via
 * `teamsService` (localStorage, scoped per orgId). Swap `teamsService` for a
 * real HTTP client when the backend grows a Team entity; the page contract
 * stays the same.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import organizationService from '../services/organizationService';
import userService from '../services/userService';
import teamsService from '../services/teamsService';
import { useTenant } from '../context/TenantContext';
import { toast } from '../components/Toast';
import TeamModal from '../components/teams/TeamModal';

const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons`;
const CHK_ICON = `${ICON_BASE}/checkouts-page`;

const COLUMNS = [
  { id: 'name',        label: 'TEAM NAME' },
  { id: 'talkGroups',  label: 'TALK GROUPS' },
  { id: 'homeGroup',   label: 'HOME GROUP' },
  { id: 'members',     label: 'MEMBERS' },
];

function tgIdOf(g)   { return g?.talkGroupId ?? g?.id; }
function tgNameOf(g) { return g?.name || g?.talkGroupName || `Talk group ${tgIdOf(g)}`; }

function TeamsPage() {
  const { orgId, currentOrg } = useTenant();
  const [teams,      setTeams]      = useState([]);
  const [talkGroups, setTalkGroups] = useState([]);
  const [users,      setUsers]      = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [query, setQuery]           = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort]             = useState({ key: 'name', asc: true });
  const [selected, setSelected]     = useState(() => new Set());

  /* -------- Load ---------------------------------------------------------- */
  const load = useCallback(async () => {
    if (!orgId) {
      setTeams([]); setTalkGroups([]); setUsers([]);
      return;
    }
    setLoading(true);
    try {
      const [teamsRes, tgRes, userRes] = await Promise.allSettled([
        teamsService.listTeams(orgId),
        organizationService.getTalkGroups(orgId),
        userService.listUsers(orgId, 0, 500),
      ]);
      const unwrap = (r) => {
        if (r.status !== 'fulfilled') return [];
        const d = r.value;
        const list = Array.isArray(d) ? d : (d?.content ?? d?.items ?? d?.data ?? []);
        return Array.isArray(list) ? list : [];
      };
      setTeams(unwrap(teamsRes));
      setTalkGroups(unwrap(tgRes));
      setUsers(unwrap(userRes));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  /* -------- Lookups ------------------------------------------------------- */
  const tgById = useMemo(() => {
    const m = new Map();
    talkGroups.forEach((g) => m.set(tgIdOf(g), g));
    return m;
  }, [talkGroups]);

  /* -------- Rows ---------------------------------------------------------- */
  const rows = useMemo(() => teams.map((t) => {
    const tgNames = (t.talkGroupIds || [])
      .map((id) => tgById.get(id))
      .filter(Boolean)
      .map(tgNameOf);
    const home = t.homeGroupId != null ? tgById.get(t.homeGroupId) : null;
    return {
      raw: t,
      id: t.id,
      name: t.name || '—',
      talkGroups: tgNames,
      talkGroupsText: tgNames.join(', '),
      homeGroup: home ? tgNameOf(home) : '—',
      members: Array.isArray(t.memberIds) ? t.memberIds.length : 0,
    };
  }), [teams, tgById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const data = !q ? rows : rows.filter((r) =>
      [r.name, r.talkGroupsText, r.homeGroup].some((v) =>
        String(v ?? '').toLowerCase().includes(q)),
    );
    return [...data].sort((a, b) => {
      const ak = sort.key === 'talkGroups' ? 'talkGroupsText' : sort.key;
      const av = a[ak]; const bv = b[ak];
      if (typeof av === 'number' && typeof bv === 'number') return (sort.asc ? 1 : -1) * (av - bv);
      return (sort.asc ? 1 : -1) * String(av ?? '').localeCompare(String(bv ?? ''));
    });
  }, [rows, query, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));

  /* -------- Selection ----------------------------------------------------- */
  const allChecked  = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someChecked = filtered.some((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) filtered.forEach((r) => next.delete(r.id));
      else            filtered.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* -------- CRUD ---------------------------------------------------------- */
  const handleAdd = () => {
    if (!orgId) {
      toast.warning('Select an organization first from the top-right site picker.');
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    const t = row.raw;
    setEditing({
      id: t.id,
      name: t.name || '',
      talkGroupIds: Array.isArray(t.talkGroupIds) ? t.talkGroupIds : [],
      memberIds:    Array.isArray(t.memberIds)    ? t.memberIds    : [],
      homeGroupId:  t.homeGroupId ?? null,
    });
    setModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete team "${row.name}"?`)) return;
    try {
      await teamsService.deleteTeam(orgId, row.id);
      toast.success('Team deleted');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete team');
    }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await teamsService.updateTeam(orgId, editing.id, form);
        toast.success('Team updated');
      } else {
        await teamsService.createTeam(orgId, form);
        toast.success('Team created');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  /* -------- Render -------------------------------------------------------- */
  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search teams"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <img src={`${CHK_ICON}/search.svg`} alt="" className="cm-icon-img" />
          </button>
          <div className="sp-heading">
            <h1>Teams</h1>
            <div className="sp-subhead"><strong>{filtered.length}</strong> TOTAL</div>
            {searchOpen && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search teams..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add team
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {!orgId ? (
        <div className="empty-state">
          <i className="fas fa-users"></i>
          <div>Select an organization from the top-right picker to view its teams.</div>
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="empty-state"><i className="fas fa-spinner fa-spin"></i><div>Loading teams…</div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-users"></i>
          <div>
            No teams yet{currentOrg ? ` for ${currentOrg.displayName || currentOrg.name}` : ''}.{' '}
            Click <strong>+ Add team</strong> to create one.
          </div>
        </div>
      ) : (
        <table className="data-table cm-table sp-table">
          <thead>
            <tr>
              <th className="cm-col-check">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              {COLUMNS.map((c) => (
                <th key={c.id}>
                  <button className="data-table-sort" onClick={() => toggleSort(c.id)}>
                    {c.label}
                    <i className={`fas fa-sort${sort.key === c.id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
                  </button>
                </th>
              ))}
              <th className="cm-col-actions" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleRow(r.id)}
                    aria-label={`Select ${r.name}`}
                  />
                </td>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td>
                  {r.talkGroups.length === 0
                    ? <span className="tg-sites-cell">—</span>
                    : (
                      <div className="tm-pill-group">
                        {r.talkGroups.map((n, i) => (
                          <span key={`${r.id}-${i}`} className="tg-team-pill">{n}</span>
                        ))}
                      </div>
                    )}
                </td>
                <td>{r.homeGroup}</td>
                <td>{r.members}</td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit team"
                    aria-label="Edit team"
                    onClick={() => handleEdit(r)}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Delete team"
                    aria-label="Delete team"
                    onClick={() => handleDelete(r)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <TeamModal
        open={modalOpen}
        initial={editing}
        talkGroups={talkGroups}
        users={users}
        saving={saving}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
      />
    </>
  );
}

export default TeamsPage;

